// Remove Keycloak's callback parameters from the address bar after login.
//
// keycloak-js is supposed to do this: `#processInit` calls `history.replaceState`
// with a cleaned URL. But it only produces one when BOTH a code (or error) AND a
// state are present —
//
//   if ((parsed.oauthParams.code || parsed.oauthParams.error) && parsed.oauthParams.state)
//
// — and only for the configured responseMode. A URL left carrying `state`,
// `session_state` and `iss` without a `code`, which is what remains once the code
// has been consumed or the page is re-entered, is never cleaned. Found in
// ginzamarkets/velocity (PR #409) on this same library version.
//
// `session_state` is the one that matters: a Keycloak SSO session identifier
// sitting in the address bar and in browser history. `code` is single-use and
// PKCE-bound (keycloak-js defaults pkceMethod to 'S256'), and `state` is a spent
// CSRF nonce. Fragments are never sent to servers, so this is address-bar and
// history exposure rather than anything reaching logs.
//
// Mirrors keycloak-js's own supportedParams so this strips exactly what the
// library would, and nothing else.
const OAUTH_PARAMS = new Set([
  'code',
  'state',
  'session_state',
  'iss',
  'access_token',
  'token_type',
  'id_token',
  'expires_in',
  'kc_action_status',
  'kc_action',
  'error',
  'error_description',
  'error_uri',
])

// Removes OAuth keys from one `a=1&b=2` string, keeping order and everything
// else untouched. Returns null when nothing was removed, so the caller can tell
// "cleaned" from "already clean" without comparing strings.
function stripParams (paramString) {
  if (!paramString) return null

  const parts = paramString.split('&').filter(Boolean)
  const kept = parts.filter((part) => !OAUTH_PARAMS.has(decodeURIComponent(part.split('=')[0])))

  return kept.length === parts.length ? null : kept.join('&')
}

// A fragment can be an OAuth callback (`#code=…&state=…`) or an ordinary anchor
// (`#pricing`). Only the former has `=` in it; an anchor is left alone.
function looksLikeParams (fragment) {
  return fragment.includes('=')
}

/**
 * Returns a cleaned href, or null when there was nothing to clean.
 *
 * Pure and idempotent: the caller decides whether to call replaceState.
 */
export function cleanAuthUrl (href) {
  let url
  try {
    url = new URL(href)
  } catch {
    return null
  }

  let changed = false

  const query = stripParams(url.search.replace(/^\?/, ''))
  if (query !== null) {
    url.search = query
    changed = true
  }

  const fragment = url.hash.replace(/^#/, '')
  if (looksLikeParams(fragment)) {
    const cleaned = stripParams(fragment)
    if (cleaned !== null) {
      url.hash = cleaned
      changed = true
    }
  }

  return changed ? url.toString() : null
}

/**
 * Strip the callback parameters from the current URL, in place.
 *
 * Called after auth init and BEFORE the router mounts: the router reads
 * window.location, so a URL cleaned afterwards would already have been routed on.
 */
export function stripAuthParamsFromUrl (history = window.history, location = window.location) {
  const cleaned = cleanAuthUrl(location.href)
  if (!cleaned) return false

  history.replaceState(history.state, '', cleaned)
  return true
}
